/* ============================================================================
 * FunctionMark — one of the sixteen immersion function marks
 * ----------------------------------------------------------------------------
 * 24 px box, 1.5 px stroke, round caps and joins, currentColor. The stroke is
 * the only ink: never a filled pictogram, never an emoji, never a second
 * weight.
 *
 *   <FunctionMark name="embassy" />
 *   <FunctionMark name="newsdesk" size={16} />
 *   <FunctionMark name="reserve" label />          // legend row
 *
 * `size` scales the box and the stroke together, so a 16 px mark keeps the
 * drawn weight it has at 24 — the stroke is expressed in the 24-unit grid, and
 * the viewBox is 24 whatever the rendered size.
 * ========================================================================= */

import type { CSSProperties, ReactNode, SVGProps } from 'react';
import { functionMark, type MarkPrimitive } from './marks';

function renderPrimitives(
  body: readonly MarkPrimitive[],
  keyPrefix = 'p',
): ReactNode[] {
  return body.map((p, i) => {
    const key = `${keyPrefix}${i}`;
    if (p.kind === 'path') return <path key={key} d={p.d} opacity={p.opacity} />;
    if (p.kind === 'circle')
      return <circle key={key} cx={p.cx} cy={p.cy} r={p.r} />;
    if (p.kind === 'rect')
      return <rect key={key} x={p.x} y={p.y} width={p.w} height={p.h} />;
    return (
      <g key={key} transform={p.transform}>
        {renderPrimitives(p.children, `${key}-`)}
      </g>
    );
  });
}

export interface FunctionMarkProps
  extends Omit<SVGProps<SVGSVGElement>, 'name' | 'children'> {
  /** Mark id (`embassy`, `news-desk`) or the registry app id (`newsdesk`). */
  name: string;
  /** Rendered box in px. The 24-unit grid scales with it. */
  size?: number;
  /** Render the mark's label beside it. */
  label?: boolean;
  /** Extra style for the wrapper when `label` is set. */
  labelStyle?: CSSProperties;
}

export function FunctionMark({
  name,
  size = 24,
  label = false,
  labelStyle,
  strokeWidth,
  style,
  ...rest
}: FunctionMarkProps) {
  const mark = functionMark(name);
  if (!mark) {
    if (import.meta.env?.DEV) {
      console.warn(`FunctionMark: unknown mark "${name}"`);
    }
    return null;
  }

  const described = rest['aria-label'] ?? rest['aria-labelledby'] ?? null;

  const svg = (
    <svg
      viewBox={mark.viewBox}
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? mark.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={described ? 'img' : undefined}
      aria-hidden={described ? undefined : true}
      focusable="false"
      data-mark={mark.id}
      data-app={mark.appId}
      style={label ? style : { ...style, flex: '0 0 auto' }}
      {...rest}
    >
      {renderPrimitives(mark.body)}
    </svg>
  );

  if (!label) return svg;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        ...labelStyle,
      }}
    >
      {svg}
      <span>{mark.label}</span>
    </span>
  );
}
